import React from 'react';
import { typeStyle } from '@carvana/showroom-css-theme';
import styles from './vehicle-coverage.module.css';

export type WarrantyHighlightProps = {
  isExpired?: boolean;
  durationLeft?: string;
  totalCoverageInMiles?: number;
  id: string;
  totalCoverageInMilesAlternativeText?: string;
  // SERV-405: VCM mileage tracking fields
  purchaseMileage?: number;
  currentMileage?: number;
  mileageLastUpdated?: string;
};

// SERV-405: 100-Day Limited Warranty mileage cap (4,189 miles)
const WARRANTY_MILEAGE_CAP = 4189;

/**
 * SERV-405: Calculate remaining warranty mileage based on VCM odometer updates
 * @returns Remaining miles if VCM has been updated, otherwise undefined for graceful degradation
 */
const calculateRemainingMileage = (
  mileageLastUpdated?: string,
  purchaseMileage?: number,
  currentMileage?: number
): number | undefined => {
  const hasVcmUpdate = !!mileageLastUpdated;

  if (!hasVcmUpdate || purchaseMileage == null || currentMileage == null) {
    return undefined;
  }

  // Don't show remaining if currentMileage equals purchaseMileage
  // This filters out purchase readings (same-day automated VCM readings)
  if (currentMileage === purchaseMileage) {
    return undefined;
  }

  const milesDriven = currentMileage - purchaseMileage;
  return Math.max(0, WARRANTY_MILEAGE_CAP - milesDriven);
};

export const WarrantyHighlight = ({
  isExpired,
  durationLeft,
  totalCoverageInMiles,
  id,
  totalCoverageInMilesAlternativeText,
  purchaseMileage,
  currentMileage,
  mileageLastUpdated,
}: WarrantyHighlightProps) => {
  const showMileageAlternativeText =
    totalCoverageInMilesAlternativeText && (totalCoverageInMiles === undefined || totalCoverageInMiles === 0);

  // SERV-405: Calculate remaining warranty mileage if VCM odometer data is available
  // Only show when warranty is not expired (respects backend's isExpired flag)
  const remainingMiles = calculateRemainingMileage(mileageLastUpdated, purchaseMileage, currentMileage);

  return (
    <div className={styles['warranty-highlight']} style={typeStyle('body-s')}>
      <div className={styles['warranty-highlight-row']}>
        <span data-testid={`${id}-days-left`}>
          {(isExpired || !durationLeft) && '0 days left'}
          {!isExpired && <span>{durationLeft}</span>}
        </span>
        <span className={styles['vertical-divider']} />
        <div className={styles['warranty-mileage']}>
          {showMileageAlternativeText ? (
            <span data-testid={`${id}-coverage-miles`}>{totalCoverageInMilesAlternativeText}</span>
          ) : (
            <span data-testid={`${id}-coverage-miles`}>
              up to {(totalCoverageInMiles || 0)?.toLocaleString()} miles
            </span>
          )}
          {remainingMiles !== undefined && !isExpired && (
            <div className={styles['warranty-remaining']}>(Est. {remainingMiles.toLocaleString()} remaining)</div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ExpiredLabel = ({ isExpired }: { isExpired?: boolean }) =>
  isExpired ? (
    <span className={styles['vehicle-coverage-expired']} style={typeStyle('label-xs')}>
      Expired
    </span>
  ) : null;
